import { useEffect, useState } from 'react';
import { HeldReleasesFilters } from '@pages/api/artist-watchlist/types';
import { KeyedMutator, useSWRFetch } from '@hooks/useSWRFetch';
import type { Request } from '@hooks/useSWRFetch/types';
import { TEN_MINUTES_IN_MS } from '@helpers/constants';
import type { Status } from '@generated/grpc/google/rpc';
import type {
  AlbumMetadataListResponse,
  Approval,
  CheckRequestStatusByAlbumResponse,
} from '@generated/grpc/spotify/artistwatchlist/v1';

const SUCCESS_CODE = 0;
const ERROR_CODE = -1;

interface UseHeldReleasesResponse<T> {
  data: T | null | undefined;
  ok: boolean | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<T>;
}

export const useHeldReleases = (
  licensorIds: string[],
  currentPage: number,
  pageSize: number,
  sortedByInsertedDateAscending: boolean,
  filter?: HeldReleasesFilters,
): UseHeldReleasesResponse<AlbumMetadataListResponse> => {
  const [offsets, setOffsets] = useState([0]);
  const searchParams = new URLSearchParams();

  searchParams.append('currentPage', currentPage.toString());
  searchParams.append('offset', offsets[currentPage - 1].toString());
  searchParams.append('pageSize', pageSize.toString());
  searchParams.append(
    'sortedByInsertedDateAscending',
    sortedByInsertedDateAscending.toString(),
  );
  searchParams.append('dateRange', filter?.onHeldDateFilter ?? '');
  licensorIds.forEach(licensorId =>
    searchParams.append('licensorIds', licensorId),
  );

  const requestObject: Request = {
    url: `catalog/api/artist-watchlist?${new URLSearchParams(searchParams)}`,
    baseUrl: '',
  };

  const result = useSWRFetch<AlbumMetadataListResponse>(
    licensorIds.length ? requestObject : null,
    {
      revalidateOnFocus: true,
      dedupingInterval: TEN_MINUTES_IN_MS,
    },
  );

  const resultOffset =
    (result.data as AlbumMetadataListResponse)?.albumMetadataListResponse
      ?.paginator?.offset || 0;

  useEffect(() => {
    setOffsets(previousOffsets => {
      const newOffsets = [...previousOffsets];
      if (typeof resultOffset !== 'undefined') {
        newOffsets[currentPage] = resultOffset;
      }
      return newOffsets;
    });
  }, [resultOffset, currentPage]);

  return result;
};

export const useHeldReleasesRequest = () => {
  const [mutationData, setMutationData] = useState<Status>();
  const [error, setError] = useState<Status>();
  const [isMutating, setIsMutating] = useState(false);

  const trigger = async (approval: Approval) => {
    setMutationData(undefined);
    setIsMutating(true);
    setError(undefined);

    const requestObject = {
      baseUrl: '',
      method: 'POST',
      body: JSON.stringify({ approval }),
    };

    try {
      const response = await fetch(
        '/catalog/api/artist-watchlist',
        requestObject,
      );
      const jsonData: Status = await response.json();

      if (jsonData.code === SUCCESS_CODE) {
        setMutationData(jsonData);
      } else {
        setError(jsonData);
      }
    } catch (e) {
      setError({
        code: ERROR_CODE,
        details: e,
      });
    }

    setIsMutating(false);
  };

  return { trigger, isMutating, mutationData, error };
};

interface useHeldReleasesRequestStatusResponse {
  response?: CheckRequestStatusByAlbumResponse;
  error?: Error;
}

export const useHeldReleasesRequestStatus = (
  albumUri: string,
): UseHeldReleasesResponse<useHeldReleasesRequestStatusResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append('albumUri', albumUri);

  const requestObject: Request = {
    url: `catalog/api/artist-watchlist/request-status?${new URLSearchParams(
      searchParams,
    )}`,
    baseUrl: '',
  };

  const result = useSWRFetch<useHeldReleasesRequestStatusResponse>(
    requestObject,
    {
      revalidateOnFocus: true,
      dedupingInterval: TEN_MINUTES_IN_MS,
    },
  );

  return result;
};
